import React, { useEffect } from 'react'
import ReactPlayer from 'react-player'
import { useTranslation } from 'react-i18next'
import { HiX, HiEye, HiClock } from 'react-icons/hi'

/**
 * VideoModal Component
 * Displays a modal with YouTube video player and details
 */
const VideoModal = ({ video, onClose }) => {
  const { t } = useTranslation()
  /**
   * Prevent body scroll when modal is open
   */
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [])

  /**
   * Close modal on Escape key
   */
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [onClose])

  /**
   * Format duration from seconds to MM:SS
   */
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  /**
   * Difficulty badge colors
   */
  const difficultyColors = {
    beginner: 'bg-green-100 text-green-800',
    intermediate: 'bg-yellow-100 text-yellow-800',
    advanced: 'bg-red-100 text-red-800'
  }

  /**
   * Handle backdrop click
   */
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4 animate-fadeIn"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-slideUp">
        {/* Header */}
        <div className="flex justify-between items-start p-4 md:p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex-1 pe-4">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
              {video.title}
            </h2>
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
              {/* Category */}
              {video.category_name && (
                <span className="flex items-center gap-1">
                  <span className="font-medium text-[#29AECB]">{video.category_name}</span>
                </span>
              )}
              <span>•</span>
              {/* Difficulty */}
              <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${difficultyColors[video.difficulty]}`}>
                {t(`tutorials.filter.${video.difficulty}`, video.difficulty)}
              </span>
              <span>•</span>
              {/* Duration */}
              <span className="flex items-center gap-1">
                <HiClock className="w-4 h-4" />
                {formatDuration(video.duration)}
              </span>
              <span>•</span>
              {/* Views */}
              <span className="flex items-center gap-1">
                <HiEye className="w-4 h-4" />
                {t('tutorials.video.views', { count: video.views_count })}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors p-2 hover:bg-gray-100 rounded-lg"
            aria-label={t('tutorials.video.close')}
          >
            <HiX className="w-6 h-6" />
          </button>
        </div>

        {/* Video Player */}
        <div className="aspect-video bg-black relative">
          <ReactPlayer
            url={video.youtube_url}
            width="100%"
            height="100%"
            controls
            playing
            config={{
              youtube: {
                playerVars: {
                  showinfo: 1,
                  modestbranding: 1,
                  rel: 0,
                  origin: window.location.origin,
                  enablejsapi: 1
                }
              }
            }}
            onError={(error) => {
              console.error('Video playback error:', error)
            }}
          />

          {/* Fallback message overlay - only shows if embed fails */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-red-600/90 text-white p-6 rounded-lg max-w-md text-center" style={{display: 'none'}} id="embed-error">
              <p className="font-semibold mb-2">{t('tutorials.video.embedErrorTitle')}</p>
              <p className="text-sm">{t('tutorials.video.embedErrorBody')}</p>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="p-4 md:p-6">
          <h3 className="font-semibold text-lg mb-3 text-gray-900">{t('tutorials.video.about')}</h3>
          <p className="text-gray-700 leading-relaxed whitespace-pre-line">
            {video.description}
          </p>

          {/* Tags */}
          {video.tags && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h4 className="font-medium text-gray-900 mb-2">{t('tutorials.video.tags')}</h4>
              <div className="flex flex-wrap gap-2">
                {video.tags.split(',').map((tag, index) => (
                  <span
                    key={index}
                    className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                  >
                    {tag.trim()}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={video.youtube_url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#FF0000] hover:bg-[#CC0000] text-white px-6 py-3 rounded-lg font-semibold transition-all transform hover:scale-105 shadow-lg flex items-center gap-2 text-lg"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
              {t('tutorials.video.watchOnYoutube')}
            </a>
            <div className="text-sm text-gray-500 flex items-center">
              <span>{t('tutorials.video.fallbackHint')}</span>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  )
}

export default VideoModal
